/**
 * LocalStorage Utilities
 *
 * Handles saving and loading game state from browser LocalStorage
 */

import type { StoredGameState, GameState } from '../types';
import { STORAGE_KEY_PREFIX, STORAGE_VERSION, COLUMNS, ROWS } from './constants';

/**
 * Get storage key for a specific date
 *
 * @param date - Puzzle date (YYYY-MM-DD)
 * @returns Storage key
 */
function getStorageKey(date: string): string {
  return `${STORAGE_KEY_PREFIX}-${date}`;
}

/**
 * Save game state to LocalStorage
 *
 * @param date - Puzzle date
 * @param state - Current game state
 */
export function saveGameState(date: string, state: GameState): void {
  const stored: StoredGameState = {
    version: STORAGE_VERSION,
    date,
    savedAt: Date.now(),
    state: {
      startTime: state.startTime,
      endTime: state.endTime,
      credits: state.credits,
      revealedClues: state.revealedClues,
      solvedColumns: state.solvedColumns,
      columnAnswers: state.columnAnswers,
      guessAttempts: state.guessAttempts,
      incorrectGuesses: state.incorrectGuesses,
      purchasedHints: state.purchasedHints,
      finalAnswerSubmitted: state.finalAnswerSubmitted,
      gameWon: state.gameWon,
      finalScore: state.finalScore,
    },
  };

  try {
    const key = getStorageKey(date);
    localStorage.setItem(key, JSON.stringify(stored));
  } catch (error) {
    console.error('Failed to save game state:', error);
  }
}

/**
 * Load game state from LocalStorage
 *
 * @param date - Puzzle date
 * @returns Stored game state or null if not found/invalid
 */
export function loadGameState(date: string): StoredGameState | null {
  try {
    const key = getStorageKey(date);
    const stored = localStorage.getItem(key);

    if (!stored) return null;

    const parsed: StoredGameState = JSON.parse(stored);

    // Validate version
    if (parsed.version !== STORAGE_VERSION) {
      console.warn('Storage version mismatch, ignoring stored state');
      return null;
    }

    // Validate date matches
    if (parsed.date !== date) {
      console.warn('Date mismatch in stored state');
      return null;
    }

    // Migrate: add purchasedHints if missing (pre-existing saves)
    if (!parsed.state.purchasedHints) {
      parsed.state.purchasedHints = (parsed.state.revealedClues || []).map(col =>
        col.map(() => false)
      );
    }

    return parsed;
  } catch (error) {
    console.error('Failed to load game state:', error);
    return null;
  }
}

/**
 * Clear game state for a specific date
 *
 * @param date - Puzzle date
 */
export function clearGameState(date: string): void {
  try {
    const key = getStorageKey(date);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to clear game state:', error);
  }
}

/**
 * Create initial game state
 *
 * @param date - Puzzle date
 * @returns Fresh game state
 */
export function createInitialGameState(date: string): GameState {
  // Initialize revealedClues grid [column][row]
  const revealedClues: boolean[][] = [];
  for (let col = 0; col < COLUMNS; col++) {
    revealedClues[col] = new Array(ROWS).fill(false);
  }

  return {
    date,
    puzzleUuid: null,
    startTime: Date.now(),
    endTime: null,
    credits: 10,
    revealedClues,
    solvedColumns: [],
    columnAnswers: [null, null, null, null],
    guessAttempts: {},
    incorrectGuesses: {},
    purchasedHints: revealedClues.map(col => col.map(() => false)),
    finalAnswerSubmitted: null,
    gameWon: null,
    finalScore: null,
    isAnimating: false,
    activeDialog: 'none',
    activeDialogColumn: null,
    activeDialogRow: null,
  };
}
